'use client';

import { useState, useEffect, useRef } from 'react';
import { MapPin, Calendar, HandHeart, ChevronRight } from 'lucide-react';
import {
  ResponsiveSheet,
  SheetPage,
  useResponsiveSheet,
  CollapsibleHeader,
} from '@church/nextjs-ui/components/ResponsiveSheet';
import type { ChurchEvent, CardTheme } from '@/types/serve-finder';
import { Img, useAssetUrl, useBgUrl } from '@/lib/asset-url';
import { BaseCard } from './BaseCard';
import { CardHero } from './CardHero';
import { CardCTA } from './CardCTA';
import {
  EventContentSlider,
  ServiceTimesSlider,
  getServiceSlideCount,
  ServiceTimesGrid,
} from './EventHelpers';
import { EventDetailContent } from './EventDetailContent';
import { ServingRoleCard } from './ServingRoleCard';

interface EventCardProps {
  event: ChurchEvent;
}

export function EventCard({ event }: EventCardProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [slideIndex, setSlideIndex] = useState(0);
  const [serviceSlideIndex, setServiceSlideIndex] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const resolve = useAssetUrl();
  const bgUrlFn = useBgUrl();

  const dateDisplay = event.End_Date ? `${event.Start_Date} – ${event.End_Date}` : event.Start_Date;

  const totalVolunteersNeeded = event.Serving_Roles.reduce(
    (sum, r) => sum + (r.Volunteers_Needed ?? 0),
    0
  );

  const hasServing = event.Serving_Roles.length > 0;
  const hasSessions = event.Sessions.length > 0;
  const hasContentSlides = event.Content_Slides != null && event.Content_Slides.length > 0;
  const hasSmartReg = event.Smart_Registration != null;
  const hasOptions = event.Registration_Options != null && event.Registration_Options.length > 0;
  const hasServiceTimes =
    hasSessions && event.Sessions.some((s) => s.Service_Times && s.Service_Times.length > 0);
  const firstSessionWithTimes = hasServiceTimes
    ? event.Sessions.find((s) => s.Service_Times && s.Service_Times.length > 0)
    : null;

  const serviceSlideCount = getServiceSlideCount(firstSessionWithTimes);

  const theme = event.Card_Theme;
  const hasFullCardTheme = !!(theme?.Body_BG_Image && event.BG_URL === theme.Body_BG_Image);
  const hasThemedBodyFooter = !!(theme?.Body_BG_Image && !hasFullCardTheme);

  const zoneStyle: React.CSSProperties = theme
    ? {
        backgroundColor: theme.Body_BG,
        backgroundImage: hasThemedBodyFooter ? bgUrlFn(theme.Body_BG_Image!) : undefined,
        backgroundSize: hasThemedBodyFooter ? 'cover' : undefined,
        backgroundPosition: hasThemedBodyFooter ? 'center' : undefined,
      }
    : {};

  const fullCardStyle: React.CSSProperties = hasFullCardTheme
    ? {
        backgroundColor: theme!.Body_BG,
        backgroundImage: bgUrlFn(theme!.Body_BG_Image!),
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : {};

  // Dot indicators overlay
  const dotIndicators = (
    <>
      {hasContentSlides && (
        <div className="absolute top-3 right-3 z-10 flex gap-1">
          {event.Content_Slides!.map((_, i) => (
            <div
              key={i}
              className={`h-1 rounded-full transition-all duration-500 ${
                i === slideIndex ? 'w-4' : 'w-1.5 bg-white/25'
              }`}
              style={
                i === slideIndex ? { backgroundColor: theme?.Accent_Color ?? '#e8734a' } : undefined
              }
            />
          ))}
        </div>
      )}
      {hasServiceTimes && serviceSlideCount > 1 && (
        <div className="absolute top-3 right-3 z-10 flex gap-1">
          {Array.from({ length: serviceSlideCount }).map((_, i) => (
            <div
              key={i}
              className={`h-1 rounded-full transition-all duration-500 ${
                i === serviceSlideIndex % serviceSlideCount ? 'w-4' : 'w-1.5'
              }`}
              style={{
                backgroundColor:
                  i === serviceSlideIndex % serviceSlideCount
                    ? (theme?.Accent_Color ?? '#d4a08a')
                    : 'rgba(255,255,255,0.25)',
              }}
            />
          ))}
        </div>
      )}
    </>
  );

  // ---- Full-card themed card (e.g. Christmas) ----
  if (hasFullCardTheme) {
    return (
      <>
        <BaseCard
          variant="standard"
          className="cursor-pointer text-left"
          style={fullCardStyle}
          onClick={() => setIsOpen(true)}
          onMouseEnter={() => videoRef.current?.play()}
          onMouseLeave={() => videoRef.current?.pause()}
          onFocus={() => videoRef.current?.play()}
          onBlur={() => videoRef.current?.pause()}
          header={
            <div className="relative flex aspect-video w-full flex-col items-center justify-center px-6">
              {event.FG_URL && (
                <Img
                  src={event.FG_URL}
                  alt={event.Title}
                  className="max-h-[60%] max-w-[80%] object-contain drop-shadow-lg"
                />
              )}
              {hasServiceTimes && firstSessionWithTimes && (
                <div className="mt-3 flex items-center gap-2.5">
                  <Img
                    src={`/${event.BG_URL.split('/')[1]}/${firstSessionWithTimes.Label.replace(/\s+/g, '')}.svg`}
                    alt=""
                    className="h-8 w-8"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                  <span
                    className="text-sm font-bold tracking-wide uppercase"
                    style={{ color: theme?.Title_Color ?? '#ffffff' }}
                  >
                    {firstSessionWithTimes.Label}
                  </span>
                </div>
              )}
              <div
                className="absolute inset-x-0 bottom-0 z-10 flex flex-wrap items-center justify-center gap-x-3 gap-y-0.5 px-4 pt-3 pb-2 text-[10px] font-semibold tracking-widest uppercase"
                style={{ color: theme?.Subtitle_Color ?? 'rgba(255,255,255,0.5)' }}
              >
                <span className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {event.Congregation_Name}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {dateDisplay}
                </span>
              </div>
            </div>
          }
          body={
            <div className="flex h-full flex-col overflow-hidden">
              {hasContentSlides ? (
                <div className="flex flex-1 items-stretch">
                  <EventContentSlider
                    slides={event.Content_Slides!}
                    theme={theme}
                    currentIndex={slideIndex}
                    onIndexChange={setSlideIndex}
                  />
                </div>
              ) : hasServiceTimes ? (
                <div className="flex-1 overflow-hidden">
                  <ServiceTimesSlider
                    event={event}
                    session={firstSessionWithTimes!}
                    theme={theme}
                    currentIndex={serviceSlideIndex}
                    onIndexChange={setServiceSlideIndex}
                  />
                </div>
              ) : (
                <div className="p-4 pb-3">
                  {event.Title && (
                    <h3
                      className="text-base leading-tight font-extrabold"
                      style={{ color: theme?.Title_Color ?? undefined }}
                    >
                      {event.Title}
                    </h3>
                  )}
                  {event.Subtitle && (
                    <p
                      className="mt-0.5 text-xs font-semibold tracking-wider uppercase"
                      style={{ color: theme?.Subtitle_Color ?? undefined }}
                    >
                      {event.Subtitle}
                    </p>
                  )}
                  <p
                    className="mt-3 line-clamp-2 text-sm leading-relaxed"
                    style={{ color: theme?.Body_Color ?? undefined }}
                  >
                    {event.Description}
                  </p>
                </div>
              )}
            </div>
          }
          footer={<CardCTA label="Details" theme={theme} />}
        >
          {dotIndicators}
        </BaseCard>
        <EventDetailSheet
          event={event}
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          hasFullCardTheme={hasFullCardTheme}
          fullCardStyle={fullCardStyle}
          bodyStyle={zoneStyle}
          theme={theme}
          dateDisplay={dateDisplay}
          hasServing={hasServing}
          hasSessions={hasSessions}
          hasSmartReg={hasSmartReg}
          hasOptions={hasOptions}
          hasServiceTimes={hasServiceTimes}
          firstSessionWithTimes={firstSessionWithTimes}
          totalVolunteersNeeded={totalVolunteersNeeded}
        />
      </>
    );
  }

  // ---- Standard card (hero + body + CTA) ----
  return (
    <>
      <BaseCard
        variant="standard"
        className="bg-card cursor-pointer text-left"
        onClick={() => setIsOpen(true)}
        onMouseEnter={() => videoRef.current?.play()}
        onMouseLeave={() => videoRef.current?.pause()}
        onFocus={() => videoRef.current?.play()}
        onBlur={() => videoRef.current?.pause()}
        bgSpan={hasThemedBodyFooter ? 'body+footer' : 'all'}
        bgStyle={hasThemedBodyFooter ? zoneStyle : undefined}
        header={
          <CardHero
            bgUrl={event.BG_URL}
            bgIsVideo={event.BG_Is_Video}
            fgUrl={event.FG_URL ?? undefined}
            fgAlt={event.Title}
            dateStrip={dateDisplay}
            campusStrip={event.Congregation_Name}
            stripColor={theme?.Subtitle_Color}
            videoRef={videoRef}
          />
        }
        body={
          <div className="flex h-full flex-col">
            {hasContentSlides ? (
              <div className="flex flex-1 items-center justify-center">
                <EventContentSlider
                  slides={event.Content_Slides!}
                  theme={theme}
                  currentIndex={slideIndex}
                  onIndexChange={setSlideIndex}
                />
              </div>
            ) : hasServiceTimes ? (
              <>
                <div
                  className="flex items-center justify-center gap-1.5 px-4 pt-3 text-[11px] font-semibold tracking-widest uppercase"
                  style={{ color: theme?.Subtitle_Color ?? 'rgba(255,255,255,0.5)' }}
                >
                  <Calendar className="h-3 w-3" />
                  {dateDisplay}
                </div>
                <div>
                  <ServiceTimesSlider
                    event={event}
                    session={firstSessionWithTimes!}
                    theme={theme}
                    currentIndex={serviceSlideIndex}
                    onIndexChange={setServiceSlideIndex}
                  />
                </div>
              </>
            ) : (
              <div className="p-4 pb-3">
                {event.Title && (
                  <h3
                    className="text-base leading-tight font-extrabold"
                    style={{ color: theme?.Title_Color ?? undefined }}
                  >
                    <span className={theme ? '' : 'text-foreground'}>{event.Title}</span>
                  </h3>
                )}
                {event.Subtitle && (
                  <p
                    className="mt-0.5 text-xs font-semibold tracking-wider uppercase"
                    style={{ color: theme?.Subtitle_Color ?? undefined }}
                  >
                    <span className={theme ? '' : 'text-muted-foreground'}>{event.Subtitle}</span>
                  </p>
                )}

                <div className="mt-2 space-y-1.5">
                  <div
                    className="flex items-center gap-1.5 text-sm"
                    style={{ color: theme?.Body_Color ?? undefined }}
                  >
                    <MapPin
                      className={`h-3.5 w-3.5 shrink-0 ${theme ? '' : 'text-muted-foreground'}`}
                    />
                    <span className={theme ? '' : 'text-muted-foreground'}>
                      {event.Congregation_Name}
                    </span>
                  </div>
                  {hasServing && (
                    <div
                      className="flex items-center gap-1.5 text-sm"
                      style={{ color: theme?.Body_Color ?? undefined }}
                    >
                      <HandHeart
                        className={`h-3.5 w-3.5 shrink-0 ${theme ? '' : 'text-muted-foreground'}`}
                      />
                      <span className={theme ? '' : 'text-muted-foreground'}>
                        {event.Serving_Roles.length} serving role
                        {event.Serving_Roles.length !== 1 && 's'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        }
        footer={<CardCTA label="Details" theme={theme} useDefaultColors={!theme} />}
      >
        {dotIndicators}
      </BaseCard>
      <EventDetailSheet
        event={event}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        hasFullCardTheme={hasFullCardTheme}
        fullCardStyle={fullCardStyle}
        bodyStyle={zoneStyle}
        theme={theme}
        dateDisplay={dateDisplay}
        hasServing={hasServing}
        hasSessions={hasSessions}
        hasSmartReg={hasSmartReg}
        hasOptions={hasOptions}
        hasServiceTimes={hasServiceTimes}
        firstSessionWithTimes={firstSessionWithTimes}
        totalVolunteersNeeded={totalVolunteersNeeded}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Event Detail Sheet
// ---------------------------------------------------------------------------

function EventDetailSheet({
  event,
  isOpen,
  onClose,
  hasFullCardTheme,
  fullCardStyle,
  bodyStyle,
  theme,
  dateDisplay,
  hasServing,
  hasSessions,
  hasSmartReg,
  hasOptions,
  hasServiceTimes,
  firstSessionWithTimes,
  totalVolunteersNeeded,
}: {
  event: ChurchEvent;
  isOpen: boolean;
  onClose: () => void;
  hasFullCardTheme: boolean;
  fullCardStyle: React.CSSProperties;
  bodyStyle: React.CSSProperties;
  theme: CardTheme | null;
  dateDisplay: string;
  hasServing: boolean;
  hasSessions: boolean;
  hasSmartReg: boolean;
  hasOptions: boolean;
  hasServiceTimes: boolean;
  firstSessionWithTimes: ReturnType<ChurchEvent['Sessions']['find']> | null;
  totalVolunteersNeeded: number;
}) {
  const resolve = useAssetUrl();
  const sheetVideoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (isOpen && sheetVideoRef.current) {
      sheetVideoRef.current.play().catch(() => {});
    }
  }, [isOpen]);

  // Build the media elements shared between blurred BG and sharp foreground
  const mediaElement = !hasFullCardTheme ? (
    event.BG_Is_Video ? (
      <video
        ref={sheetVideoRef}
        src={resolve(event.BG_URL)}
        autoPlay
        loop
        muted
        playsInline
        className="h-full w-full object-cover"
      />
    ) : (
      <Img src={event.BG_URL} alt="" className="h-full w-full object-cover" />
    )
  ) : null;

  const fgOverlay = event.FG_URL ? (
    <div className="absolute inset-0 flex items-center justify-center p-8">
      <Img
        src={event.FG_URL}
        alt={event.Title}
        className="max-h-[70%] max-w-[85%] object-contain drop-shadow-lg"
      />
    </div>
  ) : null;

  const campusDateStrip = (
    <div
      className={`absolute inset-x-0 bottom-0 flex items-center justify-center gap-3 px-4 pt-4 pb-3 text-[11px] font-semibold tracking-widest uppercase ${hasFullCardTheme ? '' : 'bg-gradient-to-t from-black/50 to-transparent'}`}
      style={{ color: theme?.Subtitle_Color ?? 'rgba(255,255,255,0.8)' }}
    >
      <span className="flex items-center gap-1">
        <MapPin className="h-3 w-3" />
        {event.Congregation_Name}
      </span>
      <span className="flex items-center gap-1">
        <Calendar className="h-3 w-3" />
        {dateDisplay}
      </span>
    </div>
  );

  const blurredBg = !hasFullCardTheme ? (
    event.BG_Is_Video ? (
      <video
        src={resolve(event.BG_URL)}
        autoPlay
        loop
        muted
        playsInline
        className="h-full w-full scale-110 object-cover blur-sm brightness-50"
      />
    ) : (
      <Img
        src={event.BG_URL}
        alt=""
        className="h-full w-full scale-110 object-cover blur-sm brightness-50"
      />
    )
  ) : undefined;

  const sharpContent = (
    <div className="relative aspect-video w-full overflow-hidden">
      {mediaElement}
      {!hasFullCardTheme && (
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-black/20" />
      )}
      {fgOverlay}
      {campusDateStrip}
    </div>
  );

  const collapsibleHeader = event.Collapsible_Header ? (
    <CollapsibleHeader ratio={0.5} background={blurredBg}>
      {sharpContent}
    </CollapsibleHeader>
  ) : (
    <div className="relative">{sharpContent}</div>
  );

  return (
    <ResponsiveSheet
      open={isOpen}
      onClose={onClose}
      maxWidth="max-w-3xl"
      noPanelPadding
      panelClassName={hasFullCardTheme ? '' : theme ? '' : 'bg-card'}
      panelStyle={hasFullCardTheme ? fullCardStyle : undefined}
      header={collapsibleHeader}
    >
      <SheetPage name="main">
        <SheetMainPage
          event={event}
          theme={theme}
          hasFullCardTheme={hasFullCardTheme}
          bodyStyle={bodyStyle}
          hasServing={hasServing}
          hasSessions={hasSessions}
          hasSmartReg={hasSmartReg}
          hasOptions={hasOptions}
          hasServiceTimes={hasServiceTimes}
          firstSessionWithTimes={firstSessionWithTimes}
          totalVolunteersNeeded={totalVolunteersNeeded}
        />
      </SheetPage>

      {hasServiceTimes && (
        <SheetPage name="rsvp">
          <RSVPPage
            event={event}
            theme={theme}
            hasFullCardTheme={hasFullCardTheme}
            bodyStyle={bodyStyle}
          />
        </SheetPage>
      )}

      {hasServing && (
        <SheetPage name="serve">
          <ServePage
            event={event}
            theme={theme}
            hasFullCardTheme={hasFullCardTheme}
            bodyStyle={bodyStyle}
          />
        </SheetPage>
      )}
    </ResponsiveSheet>
  );
}

// ---------------------------------------------------------------------------
// Sheet main page wrapper (provides navigate to EventDetailContent)
// ---------------------------------------------------------------------------

function SheetMainPage(props: React.ComponentProps<typeof EventDetailContent>) {
  const { navigate } = useResponsiveSheet();
  const hasServing = props.event.Serving_Roles.length > 0;
  return (
    <EventDetailContent
      {...props}
      onRSVP={() => navigate('rsvp')}
      onServe={hasServing ? () => navigate('serve') : undefined}
    />
  );
}

// ---------------------------------------------------------------------------
// RSVP Page (service times detail with capacity bars + amenities)
// ---------------------------------------------------------------------------

function RSVPPage({
  event,
  theme,
  hasFullCardTheme,
  bodyStyle,
}: {
  event: ChurchEvent;
  theme: CardTheme | null;
  hasFullCardTheme: boolean;
  bodyStyle: React.CSSProperties;
}) {
  const { goBack } = useResponsiveSheet();

  const session = event.Sessions.find((s) => s.Service_Times && s.Service_Times.length > 0);

  if (!session) return null;

  return (
    <div className="min-h-[200px]" style={hasFullCardTheme ? undefined : bodyStyle}>
      <div className="p-4">
        <button
          type="button"
          onClick={goBack}
          className="flex items-center gap-1 text-xs font-semibold tracking-wide uppercase transition-opacity hover:opacity-80"
          style={{ color: theme?.Accent_Color ?? '#d4a08a' }}
        >
          <ChevronRight className="h-4 w-4 rotate-180" />
          Back
        </button>
      </div>

      <div className="px-4 pb-4">
        <div className="p-4" style={{ backgroundColor: 'rgba(23, 57, 45, 0.7)' }}>
          <div className="mb-3 flex items-center gap-2.5">
            <Img
              src={`/${event.BG_URL.split('/')[1]}/${session.Label.replace(/\s+/g, '')}.svg`}
              alt=""
              className="h-7 w-7"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
            <span
              className="text-sm font-bold tracking-wide uppercase"
              style={{ color: theme?.Title_Color ?? '#ffffff' }}
            >
              {session.Label}
            </span>
          </div>
          <ServiceTimesGrid times={session.Service_Times!} theme={theme} showLegend showCapacity />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Serve Page (serving roles moved to dedicated page)
// ---------------------------------------------------------------------------

function ServePage({
  event,
  theme,
  hasFullCardTheme,
  bodyStyle,
}: {
  event: ChurchEvent;
  theme: CardTheme | null;
  hasFullCardTheme: boolean;
  bodyStyle: React.CSSProperties;
}) {
  const { goBack } = useResponsiveSheet();

  return (
    <div className="min-h-[200px]" style={hasFullCardTheme ? undefined : bodyStyle}>
      <div className="p-4">
        <button
          type="button"
          onClick={goBack}
          className="flex items-center gap-1 text-xs font-semibold tracking-wide uppercase transition-opacity hover:opacity-80"
          style={{ color: theme?.Accent_Color ?? 'var(--brand-secondary)' }}
        >
          <ChevronRight className="h-4 w-4 rotate-180" />
          Back
        </button>
      </div>

      <div className="space-y-5 px-4 pb-4">
        <div className="flex items-center gap-2">
          <HandHeart
            className="h-4 w-4"
            style={{ color: theme?.Accent_Color ?? 'var(--brand-secondary)' }}
          />
          <p
            className="text-xs font-semibold tracking-wider uppercase"
            style={{ color: theme?.Body_Color ?? undefined }}
          >
            <span className={theme ? '' : 'text-muted-foreground'}>Serve at this event</span>
          </p>
        </div>

        <div className="space-y-3">
          {event.Serving_Roles.map((role) => (
            <ServingRoleCard key={role.ID} role={role} />
          ))}
        </div>
      </div>
    </div>
  );
}
